import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader, Panel, Pill, Loading, ErrorNote, Empty, formatDate } from '../components/ui';
import { useAuth, useRight } from '../lib/auth';
import {
  COMM_FORMATS, COMM_FORMAT_LABELS, PROFILE_FIELD_KEYS, PROFILE_FIELD_LABELS,
  canApproveComms, type CommFormat, type ProfileFieldKey,
} from '@inbrief/shared';

interface Profile extends Record<string, any> {
  id: string; service_name: string; approved_at: string | null;
  approved_by_name: string | null; practice_name: string | null;
  gaps: ProfileFieldKey[];
}

interface Violation {
  field: string; fieldLabel: string; why: string; excerpt: string; matched?: string;
}

/** Marks the offending words inside the quoted sentence. */
function highlightMatch(excerpt: string, matched: string) {
  const at = excerpt.toLowerCase().indexOf(matched.toLowerCase());
  if (at === -1) return excerpt;
  return (
    <>
      {excerpt.slice(0, at)}
      <mark>{excerpt.slice(at, at + matched.length)}</mark>
      {excerpt.slice(at + matched.length)}
    </>
  );
}

interface Communication {
  id: string; format: CommFormat; service_name: string; profile_id: string;
  version_id: string | null; version_number: number; body: string | null;
  status: string; grounding_violations: Violation[]; filled_fields: string[];
}

export function Comms() {
  const { user } = useAuth();
  const canDraft = useRight('comms');
  const canApprove = user ? canApproveComms(user.role) : false;
  const queryClient = useQueryClient();

  const [selectedProfile, setSelectedProfile] = useState<string>('');
  const [format, setFormat] = useState<CommFormat>('letter');
  const [editing, setEditing] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const profiles = useQuery({
    queryKey: ['profiles'],
    queryFn: () => api.get<{ profiles: Profile[]; missingPlaceholder: string }>('/api/profiles'),
  });

  const communications = useQuery({
    queryKey: ['communications'],
    queryFn: () => api.get<{ communications: Communication[] }>('/api/communications'),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['profiles'] });
    queryClient.invalidateQueries({ queryKey: ['communications'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const createProfile = useMutation({
    mutationFn: (serviceName: string) => api.post('/api/profiles', { serviceName }),
    onSuccess: invalidate,
  });
  const saveField = useMutation({
    mutationFn: ({ id, key, value }: { id: string; key: string; value: string | null }) =>
      api.patch(`/api/profiles/${id}`, { [key]: value }),
    onSuccess: invalidate,
  });
  const approveProfile = useMutation({
    mutationFn: (id: string) => api.post(`/api/profiles/${id}/approve`),
    onSuccess: invalidate,
  });
  const generate = useMutation({
    mutationFn: () => api.post('/api/communications', { serviceProfileId: selectedProfile, format }),
    onSuccess: invalidate,
  });
  const approveComm = useMutation({
    mutationFn: (versionId: string) => api.post(`/api/communication-versions/${versionId}/approve`),
    onSuccess: invalidate,
  });
  const saveEdit = useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      api.post(`/api/communications/${id}/versions`, { body }),
    onSuccess: () => { setEditingId(null); invalidate(); },
  });

  const list = profiles.data?.profiles ?? [];
  const active = list.find((p) => p.id === (selectedProfile || list[0]?.id));
  const missing = profiles.data?.missingPlaceholder ?? '';

  return (
    <>
      <PageHeader
        title="Patient Communications"
        description="Letters, texts and appointment guides built from information your practice has approved. InBrief never sends anything to a patient — you copy or download and send it yourself."
      />

      <div className="note-box">
        <strong>InBrief does not send.</strong> There is no send, distribute or deliver function
        anywhere in this product. That is what keeps patient contact details out of it entirely.
      </div>

      {profiles.isLoading ? <Loading what="Loading service profiles" /> : null}
      <ErrorNote error={profiles.error} />

      <Panel
        title="Service profiles"
        icon="≡"
        actions={canDraft ? (
          <button
            className="btn btn-secondary btn-small"
            onClick={() => {
              const name = window.prompt('Service name, e.g. "Diabetes annual review"');
              if (name?.trim()) createProfile.mutate(name.trim());
            }}
          >
            Add a service
          </button>
        ) : null}
      >
        {list.length === 0 ? (
          <Empty>
            No services yet. A service profile holds the local information a letter needs — what
            happens, how long it takes, where to come, what to bring.
          </Empty>
        ) : (
          <div className="seg-tabs">
            {list.map((p) => (
              <button
                key={p.id}
                className={`seg-tab${p.id === active?.id ? ' active' : ''}`}
                onClick={() => setSelectedProfile(p.id)}
              >
                {p.service_name}
                {p.gaps.length ? ` (${p.gaps.length} gaps)` : ''}
              </button>
            ))}
          </div>
        )}
      </Panel>

      {active ? (
        <Panel
          title={active.service_name}
          icon="✎"
          actions={
            <span className="row-gap">
              {active.approved_at ? (
                <Pill tone="approved">Approved {formatDate(active.approved_at)}</Pill>
              ) : (
                <Pill tone="review">Not approved</Pill>
              )}
              {canApprove && !active.approved_at ? (
                <button
                  className="btn btn-primary btn-small"
                  onClick={() => approveProfile.mutate(active.id)}
                >
                  Approve profile
                </button>
              ) : null}
            </span>
          }
        >
          {active.gaps.length ? (
            <div className="warn-box">
              {active.gaps.length} field{active.gaps.length > 1 ? 's have' : ' has'} no approved
              content. Anything generated will show a visible gap there rather than a plausible
              guess — that is deliberate, and it is why a letter must never be sent unread.
            </div>
          ) : null}

          <div className="table-wrap">
            <table className="dt">
              <caption className="visually-hidden">Approved service profile fields</caption>
              <thead>
                <tr><th scope="col">Field</th><th scope="col">Approved content</th></tr>
              </thead>
              <tbody>
                {PROFILE_FIELD_KEYS.map((key) => {
                  const value = active[key] as string | null;
                  const draft = editing[`${active.id}:${key}`];
                  return (
                    <tr key={key}>
                      <th scope="row">{PROFILE_FIELD_LABELS[key]}</th>
                      <td>
                        {canDraft ? (
                          <textarea
                            rows={2}
                            value={draft ?? value ?? ''}
                            placeholder="Not provided"
                            onChange={(e) =>
                              setEditing((s) => ({ ...s, [`${active.id}:${key}`]: e.target.value }))
                            }
                            onBlur={(e) => {
                              const next = e.target.value.trim();
                              if ((value ?? '') === next) return;
                              saveField.mutate({ id: active.id, key, value: next === '' ? null : next });
                            }}
                          />
                        ) : (
                          <span>{value ?? <em className="field-hint">{missing}</em>}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {canDraft ? (
            <div className="ask-row" style={{ marginTop: 16 }}>
              <div className="field">
                <label className="field-label" htmlFor="commFormat">Format</label>
                <select id="commFormat" value={format} onChange={(e) => setFormat(e.target.value as CommFormat)}>
                  {COMM_FORMATS.map((f) => (
                    <option key={f} value={f}>{COMM_FORMAT_LABELS[f]}</option>
                  ))}
                </select>
              </div>
              <button
                className="btn btn-primary"
                disabled={generate.isPending || !active.approved_at}
                onClick={() => { setSelectedProfile(active.id); generate.mutate(); }}
                title={active.approved_at ? undefined : 'Approve the service profile first'}
              >
                {generate.isPending ? 'Drafting…' : 'Draft communication'}
              </button>
            </div>
          ) : null}
          <ErrorNote error={generate.error ?? saveField.error ?? approveProfile.error} />
        </Panel>
      ) : null}

      {communications.data?.communications.map((c) => (
        <Panel
          key={c.id}
          title={`${c.service_name} — ${COMM_FORMAT_LABELS[c.format]}`}
          icon="✉"
          actions={
            <span className="row-gap">
              <Pill tone={c.status === 'approved' ? 'approved' : c.status === 'blocked' ? 'rejected' : 'draft'}>
                {c.status}
              </Pill>
              {c.version_id ? (
                <a className="btn btn-ghost btn-small" href={`/api/communication-versions/${c.version_id}/export`}>
                  Download
                </a>
              ) : null}
              {canApprove && c.status === 'draft' && c.version_id ? (
                <button
                  className="btn btn-primary btn-small"
                  onClick={() => approveComm.mutate(c.version_id!)}
                >
                  Approve
                </button>
              ) : null}
            </span>
          }
        >
          {c.status === 'blocked' && c.grounding_violations.length ? (
            <div className="warn-box">
              <strong>Blocked — this draft states something your approved profile does not contain.</strong>
              <ul className="violation-list">
                {c.grounding_violations.map((v, i) => (
                  <li key={i}>
                    <strong>{v.fieldLabel}</strong> is not provided, but the draft {v.why}.
                    <blockquote className="violation-quote">
                      {v.matched ? highlightMatch(v.excerpt, v.matched) : v.excerpt}
                    </blockquote>
                  </li>
                ))}
              </ul>
              Fill in the missing profile field, or edit the draft, and it can be approved.
            </div>
          ) : null}

          {/*
            A blocked draft tells the reader to edit it, so there has to be a
            way to. Editing writes a new version and revalidates: an edit can
            introduce an ungrounded claim as easily as a generation can.
          */}
          {editingId === c.id ? (
            <>
              <label className="visually-hidden" htmlFor={`edit-${c.id}`}>Edit the draft</label>
              <textarea
                id={`edit-${c.id}`}
                className="comm-editor"
                rows={18}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <div className="row-gap" style={{ marginTop: 8 }}>
                <button
                  className="btn btn-primary btn-small"
                  disabled={saveEdit.isPending || draft.trim() === (c.body ?? '').trim()}
                  onClick={() => saveEdit.mutate({ id: c.id, body: draft })}
                >
                  {saveEdit.isPending ? 'Checking…' : 'Save and re-check'}
                </button>
                <button className="btn btn-ghost btn-small" onClick={() => setEditingId(null)}>
                  Cancel
                </button>
                <span className="field-hint">
                  Saving creates a new version. The previous one stays on the record.
                </span>
              </div>
            </>
          ) : (
            <>
              <pre className="preview-area">{c.body}</pre>
              {canDraft && c.status !== 'approved' ? (
                <button
                  className="btn btn-secondary btn-small"
                  style={{ marginTop: 8 }}
                  onClick={() => { setEditingId(c.id); setDraft(c.body ?? ''); }}
                >
                  Edit the draft
                </button>
              ) : null}
            </>
          )}
          <ErrorNote error={approveComm.error ?? saveEdit.error} />
        </Panel>
      ))}
    </>
  );
}
